import * as Sentry from "@sentry/tanstackstart-react";
import { op } from "#/modules/util.instrumentation/op";

/**
 * OpenPanel and GlitchTip derive OS and device from the user agent, which browsers have frozen:
 * iPadOS Safari poses as macOS 10.15.7, Safari 26 reports iOS 18.6 in its `OS` token, and
 * Chromium reports every Android as "Android 10; K", Windows 11 as 10 and a fixed ChromeOS build.
 * Where the browser still tells the truth (touch support and the Safari `Version/` token on Apple,
 * Client Hints on Chromium) we override their parse; everywhere else their own parse stands.
 * Viewport, screen and support for media range syntax (`@media (width >= 48rem)`, which all
 * Tailwind 4 breakpoints compile to and Safari only understands from 16.4) tell us why a layout
 * breaks on a specific device.
 */
interface DevicePlatform {
  os: string;
  osVersion?: string;
  deviceType?: "mobile" | "tablet" | "desktop";
  brand?: string;
  model?: string;
}

// Not in lib.dom: Client Hints only exist in Chromium.
declare global {
  interface Navigator {
    userAgentData?: {
      mobile: boolean;
      getHighEntropyValues(hints: string[]): Promise<{
        platform: string;
        platformVersion?: string;
        model?: string;
        formFactors?: string[];
      }>;
    };
  }
}

function readAppleVersion(ua: string): string | undefined {
  const osToken = /\bOS (\d+(?:_\d+)*) like Mac OS X/.exec(ua)?.[1]?.replaceAll("_", ".");
  // Safari ships with the OS, so its version is the OS release. Only trusted where the OS token is
  // missing (iPadOS desktop mode) or frozen: from iOS 26 on it stays at 18.x.
  const safari = /\) Version\/(\d+(?:\.\d+)*)/.exec(ua)?.[1];
  if (!osToken) return safari;
  const osTokenFrozen = /^1[89](?:\.|$)/.test(osToken);
  if (safari && osTokenFrozen && Number.parseInt(safari, 10) >= 26) return safari;
  return osToken;
}

function readApplePlatform(ua: string): DevicePlatform | undefined {
  // iPadOS desktop mode sends a Mac user agent. Touch alone would also match a future touchscreen
  // Mac; `navigator.standalone` only exists in iOS WebKit.
  const isIpad =
    /\biPad\b/.test(ua) ||
    (/\bMacintosh\b/.test(ua) && "standalone" in navigator && navigator.maxTouchPoints > 1);
  const isIphone = /\biP(?:hone|od)\b/.test(ua);
  if (!isIpad && !isIphone) return undefined;

  return {
    os: "iOS",
    osVersion: readAppleVersion(ua),
    deviceType: isIpad ? "tablet" : "mobile",
    brand: "Apple",
    model: isIpad ? "iPad" : "iPhone",
  };
}

function trimZeros(version: string) {
  return version.replace(/(?:\.0)+$/, "");
}

// `formFactors` (Chromium 134+) can list several; a detachable Chromebook is "Desktop" and
// "Tablet". Older Chromium only has the `mobile` flag, which says nothing about tablets.
function readDeviceType(
  formFactors: string[] | undefined,
  mobile: boolean,
): DevicePlatform["deviceType"] {
  if (formFactors?.some((factor) => factor === "Tablet" || factor === "EInk")) return "tablet";
  if (formFactors?.includes("Mobile") || mobile) return "mobile";
  if (formFactors?.includes("Desktop")) return "desktop";
  return undefined;
}

function readOs(
  platform: string,
  version: string,
): Pick<DevicePlatform, "os" | "osVersion"> | undefined {
  switch (platform) {
    case "Android":
      return { os: "Android", osVersion: trimZeros(version) };
    case "Windows": {
      // Windows reports its UniversalApiContract version: 13 and up is Windows 11, 1–10 is
      // Windows 10, 0 is anything older.
      const contract = Number.parseInt(version, 10);
      if (!(contract > 0)) return undefined;
      return { os: "Windows", osVersion: contract >= 13 ? "11" : "10" };
    }
    case "macOS":
      return { os: "macOS", osVersion: trimZeros(version) };
    case "Chrome OS":
    case "Chromium OS":
      return { os: "Chrome OS", osVersion: version };
    default:
      return undefined;
  }
}

async function readClientHintsPlatform(): Promise<DevicePlatform | undefined> {
  const uaData = navigator.userAgentData;
  if (!uaData) return undefined;

  const { platform, platformVersion, model, formFactors } = await uaData.getHighEntropyValues([
    "platformVersion",
    "model",
    "formFactors",
  ]);
  const os = platformVersion ? readOs(platform, platformVersion) : undefined;
  if (!os) return undefined;

  return {
    ...os,
    deviceType: readDeviceType(formFactors, uaData.mobile),
    model: model || undefined,
  };
}

function applyPlatform(platform: DevicePlatform) {
  // OpenPanel prefers these reserved properties over its own user agent parse.
  op.setGlobalProperties({
    __os: platform.os,
    __osVersion: platform.osVersion,
    __device: platform.deviceType,
    __brand: platform.brand,
    __model: platform.model,
  });

  // GlitchTip only parses the user agent for contexts the event doesn't carry.
  Sentry.setContext("os", { name: platform.os, version: platform.osVersion });
  if (platform.model) {
    Sentry.setContext("device", {
      family: platform.model,
      model: platform.model,
      brand: platform.brand,
    });
  }
  Sentry.setTag("os", [platform.os, platform.osVersion].filter(Boolean).join(" "));
}

function readViewport() {
  return `${window.innerWidth}x${window.innerHeight}`;
}

// The first screen view waits for this, so a stuck browser API must not hold it back.
const CLIENT_HINTS_WAIT_MS = 500;

/** Stamp device facts on every OpenPanel event and GlitchTip event. Client-only. */
export async function initClientDeviceContext() {
  const screen = `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}x`;
  // Unsupported syntax turns the query into `not all`, which never matches.
  const mediaRanges = window.matchMedia("(width >= 0px)").matches;

  op.setGlobalProperties({
    screen,
    touch_points: navigator.maxTouchPoints,
    media_ranges: mediaRanges,
    viewport: readViewport(),
  });
  // Rotation and split view change the viewport mid-session; the merge is local, nothing is sent.
  window.addEventListener("resize", () => op.setGlobalProperties({ viewport: readViewport() }));

  Sentry.setTag("media_ranges", String(mediaRanges));
  Sentry.setContext("screen", { screen, touch_points: navigator.maxTouchPoints });
  Sentry.addEventProcessor((event) => {
    event.tags = { ...event.tags, viewport: readViewport() };
    return event;
  });

  const applePlatform = readApplePlatform(navigator.userAgent);
  if (applePlatform) {
    applyPlatform(applePlatform);
    return;
  }
  const clientHints = readClientHintsPlatform()
    .then((platform) => platform && applyPlatform(platform))
    .catch(() => {});
  await Promise.race([
    clientHints,
    new Promise((resolve) => setTimeout(resolve, CLIENT_HINTS_WAIT_MS)),
  ]);
}
